import React, { useState } from 'react';
import { truncateParty, formatTime, formatNumber } from '../../utils/formatters';

export function AppRow({ app, rank, compact = false, showRewards = false }) {
  const [expanded, setExpanded] = useState(false);

  // Determine if app is active (last active within 24 hours)
  const isActive = app.lastActiveAt &&
    (Date.now() - new Date(app.lastActiveAt).getTime()) < 24 * 60 * 60 * 1000;

  if (compact) {
    // Compact view for overview page
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: showRewards
          ? '32px minmax(0, 1.5fr) 100px 70px'
          : '32px minmax(0, 1fr) minmax(0, 1fr) 80px',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-subtle)',
        alignItems: 'center',
      }}>
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '12px',
          color: rank <= 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
          fontWeight: rank <= 3 ? '700' : '400',
        }}>
          #{rank}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: '500',
            fontSize: '13px',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {app.name}
          </div>
          {showRewards && (
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '10px',
              color: 'var(--text-disabled)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {truncateParty(app.provider)}
            </div>
          )}
        </div>
        {showRewards ? (
          <div style={{
            textAlign: 'right',
            fontFamily: 'var(--font-mono)',
            fontSize: '13px',
            color: 'var(--text-secondary)',
            fontWeight: '600',
          }}>
            {app.rewards ? `${formatNumber(app.rewards)} CC` : '—'}
          </div>
        ) : (
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {truncateParty(app.provider)}
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: 'flex-end',
        }}>
          {showRewards ? (
            app.isFeatured ? (
              <>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'var(--text-primary)',
                }} />
                <span style={{
                  fontSize: '10px',
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                }}>FEAT</span>
              </>
            ) : (
              <span style={{ fontSize: '11px', color: 'var(--text-disabled)' }}>—</span>
            )
          ) : (
            <span style={{
              textAlign: 'right',
              fontSize: '11px',
              color: 'var(--text-disabled)',
            }}>
              {app.createdAt ? formatTime(new Date(app.createdAt).getTime()) : '—'}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Full view with expandable details
  return (
    <div>
      {/* Main Row - Clickable */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'grid',
          gridTemplateColumns: '32px minmax(0, 1fr) minmax(0, 150px) 80px 60px 24px',
          gap: '12px',
          padding: '12px 16px',
          borderBottom: expanded ? 'none' : '1px solid var(--border-subtle)',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
          background: expanded ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
        }}
        onMouseEnter={e => {
          if (!expanded) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
        }}
        onMouseLeave={e => {
          if (!expanded) e.currentTarget.style.background = 'transparent';
        }}
      >
        {/* Rank */}
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          color: 'var(--text-tertiary)',
        }}>
          #{rank}
        </div>

        {/* App Name + Status */}
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            fontWeight: '500',
            fontSize: '13px',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {app.name}
          </div>
          {app.hasValidatorData && (
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: isActive ? 'var(--text-primary)' : 'var(--text-disabled)',
              flexShrink: 0,
            }} title={isActive ? 'Active' : 'Inactive'} />
          )}
        </div>

        {/* Contact */}
        <div style={{
          fontSize: '11px',
          color: app.contact ? 'var(--text-secondary)' : 'var(--text-disabled)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {app.contact ? (
            <a
              href={`mailto:${app.contact}`}
              onClick={e => e.stopPropagation()}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {app.contact}
            </a>
          ) : '—'}
        </div>

        {/* Version */}
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          color: app.version ? 'var(--text-tertiary)' : 'var(--text-disabled)',
          textAlign: 'center',
        }}>
          {app.version || '—'}
        </div>

        {/* Registered */}
        <div style={{
          textAlign: 'right',
          fontSize: '11px',
          color: 'var(--text-disabled)',
        }}>
          {app.createdAt ? formatTime(new Date(app.createdAt).getTime()) : '—'}
        </div>

        {/* Expand Icon */}
        <div style={{
          color: 'var(--text-tertiary)',
          fontSize: '12px',
          transition: 'transform 0.2s ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          ▼
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{
          padding: '16px 20px 16px 60px',
          background: 'var(--bg-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
          }}>
            {/* Provider ID */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                Provider ID
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                color: 'var(--text-primary)',
                wordBreak: 'break-all',
              }}>
                {app.provider}
              </div>
            </div>

            {/* Contract ID */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                Contract ID
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                color: 'var(--text-primary)',
                wordBreak: 'break-all',
              }}>
                {truncateParty(app.contractId, 20)}
              </div>
            </div>

            {/* Sponsor */}
            {app.sponsor && (
              <div>
                <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                  Sponsor
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '11px',
                  color: 'var(--text-primary)',
                }}>
                  {app.sponsor.split('::')[0]}
                </div>
              </div>
            )}

            {/* Registered Date */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                Registered
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-primary)',
              }}>
                {app.createdAt ? new Date(app.createdAt).toLocaleString() : '—'}
              </div>
            </div>

            {/* Last Active */}
            {app.lastActiveAt && (
              <div>
                <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                  Last Active
                </div>
                <div style={{
                  fontSize: '12px',
                  color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
                }}>
                  {new Date(app.lastActiveAt).toLocaleString()}
                </div>
              </div>
            )}

            {/* Faucet Stats */}
            {app.faucetState && (
              <div>
                <div style={{ fontSize: '10px', color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                  Faucet Activity
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                  Rounds {app.faucetState.firstReceivedFor?.number || '?'} - {app.faucetState.lastReceivedFor?.number || '?'}
                  {app.faucetState.numCouponsMissed && (
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                      ({app.faucetState.numCouponsMissed} missed)
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Validator Data Badge */}
          {!app.hasValidatorData && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '4px',
              fontSize: '11px',
              color: 'var(--text-tertiary)',
            }}>
              No additional validator data available for this app
            </div>
          )}
        </div>
      )}
    </div>
  );
}
